import React, { useEffect, useMemo, useState } from 'react'
import { Container, Row, Col, Card, CardHeader, CardBody, Form, Button, Alert, FormFeedback, FormText } from 'reactstrap';
import { useParams, useSearchParams } from 'react-router-dom';
import { fetchCompanyContract } from "../../../../api/requests/payment-requests";
import ContractModal from './components/ContractModal';
import CreditCardPreview from './components/CreditCardPreview';
import PageHeader from './components/PageHeader';
import { LinkInfo, InvoiceInfo } from './components/InfoPanels';
import CustomerForm from './components/CustomerForm';
import PaymentAmountInput from './components/PaymentAmountInput';
import CardFields from './components/CardFields';
import BinStatus from './components/BinStatus';
import TermsCheckbox from './components/TermsCheckbox';
import InstallmentTable from "./components/InstallmentTable";
import { formatTRY } from '../utils/currency';
import useLinkData from './hooks/useLinkData';
import useAmountCurrency from './hooks/useAmountCurrency';
import useCardVisual from './hooks/useCardVisual';
import useBinLookup from './hooks/useBinLookup';
import useContractAcceptance from './hooks/useContractAcceptance';
import useInstantValidation from './hooks/useInstantValidation';
import defaultContractText from "../../../../constants/defaultContractText"

// Linkin para birimi → firmanın canlı kur alanı (useLinkData'nın çektiği finalRates).
const FX_RATE_KEYS = { USD: 'usdFinalRate', EUR: 'eurFinalRate', GBP: 'gbpFinalRate' };

const getTotalWithCommission = (baseAmount, commissionPercent) => {
    const amount = Number(baseAmount || 0);
    const divisor = (100 - Number(commissionPercent || 0)) / 100;
    return amount / (divisor || 1);
};

export default function InstantPaymentForm() {
    const [searchParams] = useSearchParams();
    const { linkId: routeLinkId } = useParams();
    const linkId = searchParams.get('instantuid') || routeLinkId;

    const [errorMessage, setErrorMessage] = useState('');
    const [selectedInstallment, setSelectedInstallment] = useState({ count: 1, commission: 0 });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [contractValidationError, setContractValidationError] = useState('');

    // Get linkData first
    const {
        pageLoading,
        errorMessage: linkErr,
        setErrorMessage: setAnyError,
        linkData: final_linkData,
        finalRates: final_finalRates,
        contractText: final_contractText,
        setContractText: final_setContractText
    } = useLinkData(linkId, null, selectedInstallment, formatTRY, getTotalWithCommission);



    const showCustomerForm = useMemo(() => {
        if (!final_linkData) return false;
        const hasAccount = !!final_linkData.accountId;
        const hasCompleteCustomerInfo = !!(final_linkData.invoicePhone && final_linkData.invoiceEmail && final_linkData.invoiceCompanyName);
        return hasAccount ? true : !hasCompleteCustomerInfo;
    }, [final_linkData]);

    const showOnlyEmail = useMemo(() => !!final_linkData?.accountId, [final_linkData]);

    const requiresCustomerInfo = useMemo(() => {
        if (!final_linkData) return false;
        const hasAccount = !!final_linkData.accountId;
        const hasInvoice = !!(final_linkData.invoiceCompanyName || final_linkData.invoiceTaxNumber || final_linkData.invoiceTcNumber || final_linkData.invoiceTaxOffice || final_linkData.invoiceAddress);
        return !(hasAccount || hasInvoice);
    }, [final_linkData]);

    // ========== STEP 1: cardVisual state'i oluştur ==========
    const [cardVisual, setCardVisual] = useState({
        bank: '',
        brand: '',
        type: '',
        schema: '',
        bankLogo: '',
        cardLogo: '',
        number: '•••• •••• •••• ••••',
        owner: 'AD SOYAD',
        month: 'AA',
        year: 'YY',
        cvv: '•••',
        amount: '₺ 0,00',
    });

    // ========== STEP 2: validation hook'unu oluştur ==========
    const validation = useInstantValidation({
        linkData: final_linkData,
        selectedInstallment,
        cardVisual,
        formatTRY,
        getTotalWithCommission,
        setErrorMessage,
        isSubmitting,
        setIsSubmitting,
        linkId,
        showCustomerForm,
        showOnlyEmail,
        requiresCustomerInfo,
        contractText: final_contractText
    });

    // ========== STEP 3: useCardVisual hook'unu oluştur ==========
    const card = useCardVisual({
        formatTRY,
        getTotalWithCommission,
        validation,
        selectedInstallment,
        cardVisual,
        setCardVisual
    });

    const {
        cardRef,
        isCardFlipped,
        setIsCardFlipped,
        handleOwnerChange,
        handleCardNumberChange,
        handleMonthChange,
        handleYearChange,
        handleCVVChange,
        isCardValid,
        years,
        getCardBackground
    } = card;

    const bin = useBinLookup({ setCardVisual, setSelectedInstallment, linkId });
    const { binQueryStatus, paytrError, detectedCardBrand, onBinCandidate } = bin;

    const amount = useAmountCurrency({ linkData: final_linkData, finalRates: final_finalRates, selectedInstallment, formatTRY, getTotalWithCommission, validation });
    const { displayAmount, selectedCurrency, handleAmountChange, handleCurrencyChange, totalToPayText, setDisplayAmount, setSelectedCurrency } = amount;

    // Sabit tutarlı link döviz cinsinden olabilir: tutar kendi biriminde gösterilir,
    // tahsil edilecek TL karşılığı ödeme anındaki güncel kurla hesaplanır.
    const fixedLinkCurrency = (final_linkData?.currency || 'TRY').toUpperCase();
    const fixedLinkRate = fixedLinkCurrency === 'TRY'
        ? 1
        : Number(final_finalRates?.[FX_RATE_KEYS[fixedLinkCurrency]]) || null;
    const fixedLinkTryAmount = final_linkData?.fixedAmount != null && fixedLinkRate
        ? Number(final_linkData.fixedAmount) * fixedLinkRate
        : null;

    const installmentOptions = useMemo(() => {
        if (!final_linkData?.installmentRates || !detectedCardBrand) return [];
        const maxInst = final_linkData?.cardsMaxInstallments?.[detectedCardBrand] || 1;
        return final_linkData.installmentRates
            .filter(r => r.cardBrand === detectedCardBrand && r.enabled && r.installmentCount <= maxInst)
            .sort((a, b) => a.installmentCount - b.installmentCount);
    }, [final_linkData?.installmentRates, final_linkData?.cardsMaxInstallments, detectedCardBrand]);

    useEffect(() => {
        if (!installmentOptions || installmentOptions.length === 0) return;
        const existing = installmentOptions.find(
            (opt) => opt.installmentCount === selectedInstallment.count
        );

        if (!existing) {
            const first = installmentOptions[0];
            setSelectedInstallment({
                count: first.installmentCount,
                commission: first.commissionPercent
            });
            validation.setFieldValue('installment_count', String(first.installmentCount), false);
            return;
        }

        if (existing.commissionPercent !== selectedInstallment.commission) {
            setSelectedInstallment({
                count: existing.installmentCount,
                commission: existing.commissionPercent
            });
        }
    }, [installmentOptions, selectedInstallment.count, validation.setFieldValue]);

    const contract = useContractAcceptance({ linkData: final_linkData, validation, requiresCustomerInfo, showCustomerForm });
    const { showContract, setShowContract, contractAccepted, setContractAccepted, hasReadContract, setHasReadContract, handleAcceptContract } = contract;

    const canViewContract = isCardValid && !!detectedCardBrand;

    const isCustomerFormValid = useMemo(() => {
        if (!showCustomerForm) return true;
        const ok = (name) => !!validation.values[name] && !validation.errors[name];
        if (showOnlyEmail) return ok('customer_email');
        if (requiresCustomerInfo) {
            const baseFields = ['customer_name','customer_email','customer_phone','customer_address','customer_district','customer_province','accountType'];
            const baseOk = baseFields.every(ok);
            if (!baseOk) return false;
            if (validation.values.accountType === 'SOLE_PROPRIETORSHIP') return ok('tcNumber');
            return ok('taxNumber') && ok('taxOffice');
        }
        return ok('customer_name') && ok('customer_email');
    }, [showCustomerForm, showOnlyEmail, requiresCustomerInfo, validation.values, validation.errors]);

    const canProceedBase = isCardValid && isCustomerFormValid;
    const canSubmit = useMemo(() => {
        console.log("=== canSubmit hesaplanıyor ===");
        console.log({
            canProceedBase,          // kart + müşteri formu
            contractAccepted,        // checkbox durumu
            binQueryStatus,          // paytr_not_found olabilir
            isSubmitting             // şu anda submit mi oluyor?
        });

        const result =
            canProceedBase &&
            contractAccepted &&
            binQueryStatus !== "paytr_not_found" &&
            !isSubmitting;

        console.log("→ canSubmit sonucu:", result);
        return result;
    }, [canProceedBase, contractAccepted, binQueryStatus, isSubmitting]);
    useEffect(() => {
        if (canProceedBase) {
            setContractValidationError('');
        }
    }, [canProceedBase]);

    const handleOpenContract = async (e) => {
        e.preventDefault();
        setAnyError('');
        setContractValidationError('');

        if (!canProceedBase) {
            validation.setSubmitting(true);
            validation.validateForm();
            setContractValidationError('Sözleşmeyi görebilmek için lütfen zorunlu tüm alanları eksiksiz ve hatasız doldurunuz.');
            return;
        }
        const companyId = final_linkData?.accountGroup?.companyId;
        const amountTL = parseFloat(validation.values.amount);
        if (!companyId || isNaN(amountTL) || amountTL <= 0) {
            setContractValidationError('Şirket veya tutar bilgisi eksik.');
            return;
        }
        try {
            const debtorName = requiresCustomerInfo
                ? validation.values.customer_name
                : (final_linkData.accountName || validation.values.customer_name || validation.values.cc_owner || 'Müşteri');
            const debtorEmail = showCustomerForm ? validation.values.customer_email : final_linkData.invoiceEmail;

            let invoiceCompanyName = final_linkData.invoiceCompanyName || debtorName;
            if (showCustomerForm && requiresCustomerInfo) {
                invoiceCompanyName = validation.values.companyName || validation.values.customer_name || debtorName;
            }
            const payload = {
                companyId,
                accountId: final_linkData.accountId || null,
                invoiceCompanyName: invoiceCompanyName || null,
                amountTL: Number(amountTL.toFixed(2)),
                debtorName,
                debtorEmail: debtorEmail || null
            };
            const res = await fetchCompanyContract(payload);
            const responseJson = await res.json().catch(() => ({}));
            const { data, statusCode, message } = responseJson;
            if (!res.ok || statusCode !== 200 || !data) {
                console.warn("⚠️ API sözleşme alınamadı, örnek metin gösteriliyor:", message);
                final_setContractText(defaultContractText);
                setShowContract(true);
                return;
            }
            final_setContractText(data);
            setShowContract(true);
        } catch (err) {
            setContractValidationError(err.message || 'Sözleşme alınırken teknik bir hata oluştu.');
        }
    };

    useEffect(() => {
        if (final_linkData?.fixedAmount == null) return;
        // Döviz linkte kur henüz yüklenmediyse bekle; kur geldiğinde effect yeniden çalışır.
        if (fixedLinkRate == null) return;

        const amt = Number(final_linkData.fixedAmount) || 0;
        // Formik'teki amount her zaman TL'dir (POS TL çeker); input ise linkin biriminde gösterilir.
        validation.setFieldValue('amount', (amt * fixedLinkRate).toFixed(2), false);
        const formattedDisplay = amt.toLocaleString('tr-TR', {
            useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2
        });
        setDisplayAmount(formattedDisplay);
        setSelectedCurrency(fixedLinkCurrency === 'TRY' ? 'TL' : fixedLinkCurrency);
    }, [final_linkData?.fixedAmount, fixedLinkCurrency, fixedLinkRate, validation]);

    useEffect(() => {
        // Bu, handleSubmit çağrıldığında (veya bir alanda değişiklik olduğunda) tetiklenir.
        // validation.isSubmitting Formik'in dahili durumudur ve bu logu sadece deneme amaçlı kullanın.
        if (validation.isSubmitting) {
            console.log('--- FORMIK SENKRON/ASENKRON VALIDASYON KONTROLÜ ---');
            console.log('validation.isValid:', validation.isValid);
            console.log('validation.errors:', validation.errors);
            console.log('validation.values:', validation.values); // Tüm form değerlerini görün
            console.log('validation.touched:', validation.touched); // Hangi alanlara dokunulmuş
        }
    }, [validation.isSubmitting, validation.isValid, validation.errors, validation.values, validation.touched]);

    if (pageLoading) {
        return (
            <Container className="mt-5 text-center">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2">Yükleniyor...</p>
            </Container>
        );
    }

    if (!final_linkData && linkErr) {
        return (
            <Container className="mt-5">
                <Row className="justify-content-center">
                    <Col md={6}>
                        <Alert color="danger" className="text-center p-5">
                            <i className="bx bx-error-circle display-4 d-block mb-3"></i>
                            <h4>Hata!</h4>
                            <p>{linkErr}</p>
                        </Alert>
                    </Col>
                </Row>
            </Container>
        );
    }

    return (

        <Container fluid className="mt-4">
            <Row><Col><PageHeader companyName={final_linkData?.accountGroup?.companyName} accountName={final_linkData?.accountName} /></Col></Row>

            {final_linkData && (
                <Row className="justify-content-center">
                    <Col md={10} lg={8}><LinkInfo description={final_linkData.description} expiryDate={final_linkData.expiryDate} /></Col>
                </Row>
            )}

            <Row className="justify-content-center">
                <Col md={10} lg={8}>
                    <Row>
                        <Col md={7} lg={8} className="pb-5">
                            <Form
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    validation.setTouched(Object.keys(validation.values).reduce((acc, k) => ({ ...acc, [k]: true }), {}), true);
                                    validation.handleSubmit();
                                    return false;
                                }}
                            >
                                {!showCustomerForm && final_linkData && (
                                    <InvoiceInfo companyName={final_linkData.invoiceCompanyName} email={final_linkData.invoiceEmail} phone={final_linkData.invoicePhone} />
                                )}

                                {showCustomerForm && (
                                    <CustomerForm formik={validation} showOnlyEmail={showOnlyEmail} requiresCustomerInfo={requiresCustomerInfo} />
                                )}

                                <Card className="mb-4">
                                    <CardHeader className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                        <h5 className="mb-0"><i className="bx bx-credit-card me-2" />Ödeme Bilgileri</h5>
                                        <small className="text-muted">
                                            <strong>USD:</strong> {final_finalRates.usdFinalRate ? `${parseFloat(final_finalRates.usdFinalRate).toFixed(2)} TL` : '-'} |
                                            <strong> EUR:</strong> {final_finalRates.eurFinalRate ? `${parseFloat(final_finalRates.eurFinalRate).toFixed(2)} TL` : '-'} |
                                            <strong> GBP:</strong> {final_finalRates.gbpFinalRate ? `${parseFloat(final_finalRates.gbpFinalRate).toFixed(2)} TL` : '-'}
                                        </small>
                                    </CardHeader>
                                    <CardBody>
                                        <PaymentAmountInput
                                            displayAmount={displayAmount}
                                            onAmountChange={handleAmountChange}
                                            selectedCurrency={selectedCurrency}
                                            onCurrencyChange={handleCurrencyChange}
                                            readOnly={!!final_linkData?.fixedAmount}
                                            formikErrorTouched={{ error: validation.errors.amount, touched: validation.touched.amount }}
                                        />
                                        {validation.touched.amount && validation.errors.amount && (<FormFeedback className="d-block">{validation.errors.amount}</FormFeedback>)}
                                        {!final_linkData?.fixedAmount && (<FormText color="info"><i className="bx bx-info-circle me-1" />Para birimini yandaki seçimden değiştirebilirsiniz.</FormText>)}
                                        {!final_linkData?.fixedAmount && (final_linkData?.minAmount != null || final_linkData?.maxAmount != null) && (
                                            <small className="text-muted d-block mt-1">
                                                {final_linkData?.minAmount != null && (<>En az ₺{Number(final_linkData.minAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</>)}
                                                {final_linkData?.minAmount != null && final_linkData?.maxAmount != null && ' — '}
                                                {final_linkData?.maxAmount != null && (<>en fazla ₺{Number(final_linkData.maxAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</>)}
                                            </small>
                                        )}
                                        {final_linkData && final_linkData.fixedAmount && (<small className="text-muted d-block mt-1">Bu bağlantı için tutar sabitlenmiştir.</small>)}
                                        {final_linkData?.fixedAmount != null && fixedLinkCurrency !== 'TRY' && (
                                            fixedLinkTryAmount != null ? (
                                                <small className="text-info d-block mt-1">
                                                    <i className="bx bx-info-circle me-1" />
                                                    {Number(final_linkData.fixedAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {fixedLinkCurrency}
                                                    {' × '}{fixedLinkRate.toFixed(2)}{' = '}<strong>{formatTRY(fixedLinkTryAmount)}</strong>
                                                    {' '}tahsil edilecektir. Kur ödeme anında geçerli olandır.
                                                </small>
                                            ) : (
                                                <small className="text-danger d-block mt-1">
                                                    <i className="bx bx-error-circle me-1" />
                                                    {fixedLinkCurrency} kuru alınamadı, ödeme şu anda yapılamıyor.
                                                </small>
                                            )
                                        )}

                                        <CardFields
                                            formik={validation}
                                            values={validation.values}
                                            errors={validation.errors}
                                            touched={validation.touched}
                                            onOwnerChange={handleOwnerChange}
                                            onNumberChange={(e) => handleCardNumberChange(e, onBinCandidate)}
                                            onMonthChange={handleMonthChange}
                                            onYearChange={handleYearChange}
                                            onCVVChange={handleCVVChange}
                                            onCVVFocus={() => setIsCardFlipped(true)}
                                            onCVVBlur={() => setIsCardFlipped(false)}
                                            years={years}
                                            helper={!detectedCardBrand && (<medium className="text-muted mt-1 d-block"><i className="bx bx-info-circle me-1" />Kartınıza özel taksit opsiyonlarını görmek için lütfen kart numaranızı giriniz.</medium>)}
                                        />

                                        {!!installmentOptions.length ? (
                                            <div className="mb-3" style={{ maxHeight: 300, overflowY: 'auto' }}>
                                                <InstallmentTable
                                                    options={installmentOptions}
                                                    selectedCount={selectedInstallment.count}
                                                    onSelect={(cnt, com) => { setSelectedInstallment({ count: cnt, commission: com }); validation.setFieldValue('installment_count', String(cnt)); }}
                                                    amount={parseFloat((validation.values.amount || '0').toString().replace(',', '.')) || 0}
                                                />
                                            </div>
                                        ) : (
                                            detectedCardBrand && (<div className="alert alert-warning my-3">{detectedCardBrand} için taksit seçeneği bulunmamaktadır. Tek çekim yapılacaktır.</div>)
                                        )}

                                        {totalToPayText && (<Alert color="info" className="mb-3"><strong>Ödenecek Toplam Tutar:</strong> {totalToPayText}</Alert>)}

                                        <BinStatus status={binQueryStatus} bank={cardVisual.bank} brand={cardVisual.brand} error={paytrError} />

                                        <TermsCheckbox
                                            checked={contractAccepted}
                                            canCheck={isCardValid && contractAccepted}
                                            hasReadContract={hasReadContract}
                                            onChange={setContractAccepted}
                                            onOpenContract={handleOpenContract}
                                        />

                                        <div className="d-grid">
                                            <Button
                                                color="success"
                                                size="lg"
                                                disabled={!canSubmit}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    validation.setTouched(Object.keys(validation.values).reduce((acc, k) => ({ ...acc, [k]: true }), {}), true);
                                                    validation.handleSubmit();
                                                }}
                                            >
                                                {isSubmitting ? (<><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />İşleniyor...</>) : (<><i className="bx bx-check-circle me-2" />ÖDEMEYİ TAMAMLA</>)}
                                            </Button>
                                        </div>

                                        {(errorMessage || linkErr || contractValidationError) && (<Alert color="danger" className="mt-2">{errorMessage || linkErr || contractValidationError}</Alert>)}
                                    </CardBody>
                                </Card>
                            </Form>
                        </Col>

                        <Col md={5} lg={4}>
                            <CreditCardPreview cardVisual={cardVisual} isFlipped={isCardFlipped} getCardBackground={getCardBackground} ref={cardRef} />
                        </Col>
                    </Row>
                </Col>
            </Row>

            <ContractModal
                open={showContract}
                onClose={() => setShowContract(false)}
                onAccept={handleAcceptContract}
                contractText={final_contractText}
                onFullyRead={() => setHasReadContract(true)}
            />
        </Container>
    );
}